import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Typography,
  Row,
  Col,
  Descriptions,
  Table,
  Tag,
  Space,
  Modal,
  message,
  Avatar,
  Divider,
  Spin,
  Timeline,
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  HistoryOutlined,
  SettingOutlined,
  LockOutlined,
  SaveOutlined,
  EyeOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/authService';
import { QuotationService, QuotationStatusHistoryEntry } from '../services/quotationService';
import WalletPanel from '../components/wallet/WalletPanel';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone?: string;
  type: string;
  isSuper: boolean;
  traderCode?: string;
  dateEntry: string;
  lastLoginDate?: string;
}

interface TraderInfo {
  TraderCode: string;
  TraderName: string;
  Address1: string;
  Address2: string;
  Address3: string;
  Tel: string;
  Email: string;
  PriceLevel: string;
  CreditLimit: number;
  PaymentTerm: string;
}

interface UserQuotation {
  RefNo: string;
  TraderName: string;
  DateIssued: string;
  TotalDueAmt: number;
  GSTAmt: number;
  webStatus: string;
  CustomerRefund?: CustomerRefund | null;
}

/** A cancelled card order's refund, as the customer sees it (issue #141). */
interface CustomerRefund {
  status: 'processing' | 'refunded';
  amount: number;
  /** Repaid by bank transfer after the card refund failed (issue #171). */
  method?: 'bank_transfer';
  expectedBy?: string;
  refundedAt?: string;
}

/**
 * The refund line under a cancelled card order. The date is our estimate from
 * the 5-7 working days card refunds take, so it says so; a refund staff are
 * still sorting out shows as processing, with no date.
 */
const RefundNote: React.FC<{ refund?: CustomerRefund | null; detailed?: boolean }> = ({
  refund,
  detailed,
}) => {
  if (!refund) return null;
  const amount = `$${Number(refund.amount || 0).toFixed(2)}`;

  if (refund.status === 'refunded') {
    return (
      <Text type="success" style={{ display: 'block', fontSize: detailed ? 14 : 12 }}>
        {amount} refunded {refund.method === 'bank_transfer' ? 'by bank transfer' : 'to your card'}
        {refund.refundedAt ? ` on ${dayjs(refund.refundedAt).format('D MMM YYYY')}` : ''}
      </Text>
    );
  }

  return (
    <Text type="secondary" style={{ display: 'block', fontSize: detailed ? 14 : 12 }}>
      {refund.expectedBy
        ? `Refund of ${amount} expected by ${dayjs(refund.expectedBy).format('D MMM YYYY')}`
        : `Refund of ${amount} being processed`}
      {detailed && refund.expectedBy && (
        <>
          <br />
          This is an estimate: card refunds usually take 5-7 working days, and your bank's
          processing time may vary.
        </>
      )}
    </Text>
  );
};

const Profile: React.FC = () => {
  // isGuest is a top-level store flag, not a property of `user`.
  const { user, isGuest } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState<{
    user: UserProfile | null;
    traderInfo: TraderInfo | null;
  }>({ user: null, traderInfo: null });
  const [quotations, setQuotations] = useState<UserQuotation[]>([]);
  const [quotationsLoading, setQuotationsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Modal states
  const [profileEditVisible, setProfileEditVisible] = useState(false);
  const [passwordChangeVisible, setPasswordChangeVisible] = useState(false);
  const [quotationDetailVisible, setQuotationDetailVisible] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState<any>(null);
  
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    if (user && !isGuest) {
      loadProfileData();
      loadUserQuotations();
    }
  }, [user]);

  // Handle URL tab parameter
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      const profile = await authService.getProfile();
      setProfileData(profile);
      
      // Set form values for editing
      profileForm.setFieldsValue({
        name: profile.user?.name,
        email: profile.user?.email,
        phone: profile.user?.phone,
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      message.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const loadUserQuotations = async () => {
    try {
      setQuotationsLoading(true);
      // This would be a new endpoint to get user's quotations
      // For now, using a placeholder - we'll need to implement this in the backend
      const userQuotations = await QuotationService.getUserQuotations();
      setQuotations(userQuotations);
    } catch (error) {
      console.error('Failed to load quotations:', error);
      // Don't show error message as this might not be implemented yet
      setQuotations([]);
    } finally {
      setQuotationsLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    let values;
    try {
      values = await profileForm.validateFields();
    } catch {
      return; // Field-level messages are already shown by the form
    }

    try {
      const resultMessage = await authService.updateProfile(values);
      message.success(resultMessage);
      setProfileEditVisible(false);
      loadProfileData();
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      message.error(
        error?.response?.data?.message || error?.message || 'Failed to update profile'
      );
    }
  };

  const handlePasswordChange = async () => {
    try {
      const values = await passwordForm.validateFields();
      await authService.changePassword(values.currentPassword, values.newPassword);
      message.success('Password changed successfully');
      setPasswordChangeVisible(false);
      passwordForm.resetFields();
    } catch (error) {
      console.error('Failed to change password:', error);
      message.error('Failed to change password');
    }
  };

  const handleViewQuotation = async (refNo: string) => {
    try {
      setLoading(true);
      const quotationDetail = await QuotationService.getQuotationDetail(refNo);
      setSelectedQuotation(quotationDetail);
      setQuotationDetailVisible(true);
    } catch (error) {
      console.error('Failed to load quotation details:', error);
      message.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'ORDER': 'blue',
      'PROCESSING': 'orange',
      'CONFIRMED': 'green',
      'SHIPPED': 'cyan',
      'COMPLETED': 'purple',
      'CANCELLED': 'red',
    };
    return colors[status] || 'default';
  };

  const quotationColumns = [
    {
      title: 'Order No.',
      dataIndex: 'RefNo',
      key: 'RefNo',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'DateIssued',
      key: 'DateIssued',
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      title: 'Amount',
      dataIndex: 'TotalDueAmt',
      key: 'TotalDueAmt',
      render: (amount: number) => `$${amount.toFixed(2)}`,
    },
    {
      title: 'GST',
      dataIndex: 'GSTAmt',
      key: 'GSTAmt',
      render: (amount: number) => `$${amount.toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'webStatus',
      key: 'webStatus',
      render: (status: string, record: UserQuotation) => (
        <>
          <Tag color={getStatusColor(status)}>{status}</Tag>
          <RefundNote refund={record.CustomerRefund} />
        </>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: UserQuotation) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewQuotation(record.RefNo)}
        >
          View
        </Button>
      ),
    },
  ];

  if (!user || isGuest) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <Title level={3}>Access Denied</Title>
        <Text>Please log in to view your profile.</Text>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Avatar size={80} icon={<UserOutlined />} style={{ marginBottom: '16px' }} />
              <Title level={2} style={{ margin: 0 }}>
                {profileData.user?.name || user.name}
              </Title>
              <Text type="secondary">{profileData.user?.email || user.email}</Text>
            </div>

            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab} 
              size="large"
              items={[
                {
                  label: (
                    <span>
                      <UserOutlined />
                      Profile Information
                    </span>
                  ),
                  key: 'profile',
                  children: (
                    <Spin spinning={loading}>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                          <Card 
                            title="Personal Information" 
                            size="small"
                            extra={
                              <Button 
                                type="text" 
                                icon={<EditOutlined />}
                                onClick={() => setProfileEditVisible(true)}
                              >
                                Edit
                              </Button>
                            }
                          >
                            <Descriptions column={1} size="small">
                              <Descriptions.Item label="Name">
                                {profileData.user?.name || 'Not provided'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Email">
                                {profileData.user?.email || 'Not provided'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Phone">
                                {profileData.user?.phone || 'Not provided'}
                              </Descriptions.Item>
                              <Descriptions.Item label="User Type">
                                <Tag color={profileData.user?.type === 'ADMIN' ? 'red' : 'blue'}>
                                  {profileData.user?.type}
                                </Tag>
                              </Descriptions.Item>
                              <Descriptions.Item label="Member Since">
                                {profileData.user?.dateEntry ? 
                                  dayjs(profileData.user.dateEntry).format('MMM D, YYYY') : 
                                  'N/A'
                                }
                              </Descriptions.Item>
                              <Descriptions.Item label="Last Login">
                                {profileData.user?.lastLoginDate ? 
                                  dayjs(profileData.user.lastLoginDate).format('MMM D, YYYY HH:mm') : 
                                  'Never'
                                }
                              </Descriptions.Item>
                            </Descriptions>
                          </Card>
                        </Col>

                        {profileData.traderInfo && (
                          <Col xs={24} lg={12}>
                            <Card title="Trading Information" size="small">
                              <Descriptions column={1} size="small">
                                <Descriptions.Item label="Trader Code">
                                  {profileData.traderInfo.TraderCode}
                                </Descriptions.Item>
                                <Descriptions.Item label="Company Name">
                                  {profileData.traderInfo.TraderName}
                                </Descriptions.Item>
                                <Descriptions.Item label="Address">
                                  {[
                                    profileData.traderInfo.Address1,
                                    profileData.traderInfo.Address2,
                                    profileData.traderInfo.Address3
                                  ].filter(Boolean).join(', ')}
                                </Descriptions.Item>
                                <Descriptions.Item label="Phone">
                                  {profileData.traderInfo.Tel}
                                </Descriptions.Item>
                                <Descriptions.Item label="Price Level">
                                  <Tag color="green">{profileData.traderInfo.PriceLevel}</Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Credit Limit">
                                  ${profileData.traderInfo.CreditLimit}
                                </Descriptions.Item>
                                <Descriptions.Item label="Payment Terms">
                                  {profileData.traderInfo.PaymentTerm}
                                </Descriptions.Item>
                              </Descriptions>
                            </Card>
                          </Col>
                        )}
                      </Row>
                    </Spin>
                  ),
                },
                {
                  label: (
                    <span>
                      <HistoryOutlined />
                      Order History
                    </span>
                  ),
                  key: 'orders',
                  children: (
                    <Card title="Your Orders">
                      <Table
                        columns={quotationColumns}
                        dataSource={quotations}
                        rowKey="RefNo"
                        loading={quotationsLoading}
                        scroll={{ x: 'max-content' }}
                        pagination={{
                          pageSize: 10,
                          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
                        }}
                        locale={{
                          emptyText: quotationsLoading ? 'Loading...' : 'No orders yet'
                        }}
                      />
                    </Card>
                  ),
                },
                {
                  label: (
                    <span>
                      <WalletOutlined />
                      My Wallet
                    </span>
                  ),
                  key: 'wallet',
                  children: <WalletPanel />,
                },
                {
                  label: (
                    <span>
                      <SettingOutlined />
                      Account Settings
                    </span>
                  ),
                  key: 'settings',
                  children: (
                    <Card>
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div>
                          <Title level={4}>Security</Title>
                          <Button 
                            icon={<LockOutlined />}
                            onClick={() => setPasswordChangeVisible(true)}
                          >
                            Change Password
                          </Button>
                        </div>

                        <div>
                          <Title level={4}>Account Information</Title>
                          <Descriptions column={1}>
                            <Descriptions.Item label="User ID">
                              {user.id}
                            </Descriptions.Item>
                            <Descriptions.Item label="Account Type">
                              {user.type}
                            </Descriptions.Item>
                            <Descriptions.Item label="Price Level">
                              {user.priceLevel}
                            </Descriptions.Item>
                          </Descriptions>
                        </div>
                      </Space>
                    </Card>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Profile Edit Modal */}
      <Modal
        title="Edit Profile"
        open={profileEditVisible}
        onOk={handleProfileUpdate}
        onCancel={() => setProfileEditVisible(false)}
        okText="Save Changes"
        okButtonProps={{ icon: <SaveOutlined /> }}
      >
        <Form form={profileForm} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter your name' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
              { max: 50, message: 'Email must be 50 characters or less' }
            ]}
            extra="An address already used by another account will be rejected. Changing it means verifying the new address before it can claim past guest orders."
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone"
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Password Change Modal */}
      <Modal
        title="Change Password"
        open={passwordChangeVisible}
        onOk={handlePasswordChange}
        onCancel={() => setPasswordChangeVisible(false)}
        okText="Change Password"
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[{ required: true, message: 'Please enter your current password' }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter your new password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>

      {/* Quotation Detail Modal */}
      <Modal
        title={`Order Details - ${selectedQuotation?.RefNo}`}
        open={quotationDetailVisible}
        onCancel={() => setQuotationDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setQuotationDetailVisible(false)}>
            Close
          </Button>
        ]}
        width="min(800px, 94vw)"
      >
        {selectedQuotation && (
          <div>
            <Descriptions bordered column={{ xs: 1, sm: 2 }} style={{ marginBottom: '16px' }}>
              <Descriptions.Item label="Order No">{selectedQuotation.RefNo}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(selectedQuotation.webStatus)}>
                  {selectedQuotation.webStatus}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Date">{dayjs(selectedQuotation.DateIssued).format('MMM D, YYYY')}</Descriptions.Item>
              <Descriptions.Item label="Total Amount">${selectedQuotation.TotalDueAmt?.toFixed(2)}</Descriptions.Item>
              {selectedQuotation.CustomerRefund && (
                <Descriptions.Item label="Refund" span={2}>
                  <RefundNote refund={selectedQuotation.CustomerRefund} detailed />
                </Descriptions.Item>
              )}
            </Descriptions>
            
            {selectedQuotation.items && selectedQuotation.items.length > 0 && (
              <Table
                size="small"
                dataSource={selectedQuotation.items}
                rowKey="LineNO"
                pagination={false}
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: 'Item', dataIndex: 'Description', key: 'Description' },
                  { title: 'Qty', dataIndex: 'Qty', key: 'Qty', align: 'right' },
                  { title: 'Price', dataIndex: 'Price', key: 'Price', render: (price: any) => `$${parseFloat(price || 0).toFixed(2)}`, align: 'right' },
                  { title: 'Total', dataIndex: 'ItemNetAmt', key: 'ItemNetAmt', render: (amount: any) => `$${parseFloat(amount || 0).toFixed(2)}`, align: 'right' },
                ]}
              />
            )}

            {selectedQuotation.statusHistory && selectedQuotation.statusHistory.length > 0 && (
              <>
                <Divider />
                <Typography.Title level={5}>Order Timeline</Typography.Title>
                <Timeline
                  items={selectedQuotation.statusHistory.map((h: QuotationStatusHistoryEntry) => ({
                    color: getStatusColor(h.ToStatus),
                    children: (
                      <div>
                        <Tag color={getStatusColor(h.ToStatus)}>{h.ToStatus}</Tag>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {dayjs(h.ChangedAt).format('MMM D, YYYY h:mm A')}
                        </Typography.Text>
                        {h.Notes && (
                          <div style={{ marginTop: 4 }}>
                            <Typography.Text>{h.Notes}</Typography.Text>
                          </div>
                        )}
                      </div>
                    ),
                  }))}
                />
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Profile;